// ==========================================================
// Streaks.js – Premium streaks + badges + featured badge
// - Journal streak (days with journal entries)
// - Goal streak (days with goal activity)
// - Journal total entry badges
// - Completed goal badges
// - All badges saved under users/{uid}.badges in Firestore
// - Badges are premium gated: hidden when not premium, but
//   never deleted, so they come back if user returns to premium
// - User can pick a featured badge stored in users/{uid}.featuredBadge
// ==========================================================

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";

// ---------- Badge configuration ----------

// Journal streak milestones
const JOURNAL_STREAK_BADGES = [
  { days: 1, label: "Getting Started", emoji: "🌱" },
  { days: 3, label: "Warm Up", emoji: "🔥" },
  { days: 7, label: "1 Week Streak", emoji: "⭐" },
  { days: 14, label: "2 Week Warrior", emoji: "⚔️" },
  { days: 30, label: "1 Month Master", emoji: "💎" },
  { days: 50, label: "Consistency King", emoji: "👑" },
  { days: 100, label: "Unstoppable", emoji: "🚀" },
];

// Goal streak milestones (days with goal activity)
const GOAL_STREAK_BADGES = [
  { days: 1, label: "Goal Setter", emoji: "🎯" },
  { days: 3, label: "Focused", emoji: "🎯🔥" },
  { days: 7, label: "Goal Grinder", emoji: "💪" },
  { days: 14, label: "Goal Machine", emoji: "⚙️" },
  { days: 30, label: "Goal Master", emoji: "🧠" },
  { days: 50, label: "Relentless", emoji: "🐺" },
  { days: 100, label: "Limitless", emoji: "🏆" },
];

// Journal total entry milestones
const JOURNAL_TOTAL_BADGES = [
  { total: 5, label: "Starter Writer", emoji: "✏️" },
  { total: 20, label: "Growing Writer", emoji: "📘" },
  { total: 50, label: "Journaler", emoji: "📗" },
  { total: 100, label: "Writing Beast", emoji: "📙" },
  { total: 250, label: "Ink Master", emoji: "📚" },
  { total: 500, label: "Eternal Writer", emoji: "📝" },
];

// Completed goals milestones
const GOAL_TOTAL_BADGES = [
  { total: 1, label: "First Win", emoji: "🥇" },
  { total: 5, label: "Goal Crusher", emoji: "⚡" },
  { total: 10, label: "Achievement Hunter", emoji: "🎖️" },
  { total: 20, label: "Progress Pro", emoji: "🧩" },
  { total: 50, label: "Dominator", emoji: "🔱" },
  { total: 100, label: "Goal Legend", emoji: "🏅" },
];

// Helper to normalise a JS Date to midnight (local)
const toDayKey = (d) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
};

// Calculate streak from a list of JS Date objects
// This looks for consecutive days going backwards
const computeStreakFromDates = (dates) => {
  if (!dates || dates.length === 0) {
    return { streak: 0, lastDate: null };
  }

  // Normalise to day-only keys and dedupe
  const dayKeys = Array.from(
    new Set(
      dates
        .filter(Boolean)
        .map((d) => toDayKey(d))
    )
  ).sort((a, b) => b - a); // newest first

  if (dayKeys.length === 0) {
    return { streak: 0, lastDate: null };
  }

  const todayKey = toDayKey(new Date());
  let streak = 0;
  let lastDateKey = dayKeys[0];

  // Allow the streak to start from today or yesterday
  if (dayKeys[0] === todayKey || dayKeys[0] === todayKey - 86400000) {
    streak = 1;
  } else {
    // Latest activity is older than yesterday, no active streak
    return { streak: 0, lastDate: new Date(dayKeys[0]) };
  }

  for (let i = 1; i < dayKeys.length; i++) {
    const diff = dayKeys[i - 1] - dayKeys[i];
    if (diff === 86400000) {
      // Exactly 1 day apart, continue streak
      streak += 1;
      lastDateKey = dayKeys[i];
    } else if (diff > 86400000) {
      // Gap detected, streak broken
      break;
    }
  }

  return { streak, lastDate: new Date(lastDateKey) };
};

export default function Streaks() {
  const auth = getAuth();
  const user = auth.currentUser;

  const [journalEntries, setJournalEntries] = useState([]);
  const [goalItems, setGoalItems] = useState([]);

  const [journalLoading, setJournalLoading] = useState(true);
  const [goalLoading, setGoalLoading] = useState(true);

  const [journalStreak, setJournalStreak] = useState(0);
  const [goalStreak, setGoalStreak] = useState(0);

  const [lastJournalDate, setLastJournalDate] = useState(null);
  const [lastGoalDate, setLastGoalDate] = useState(null);

  const [isPremium, setIsPremium] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const [userBadges, setUserBadges] = useState({
    journalStreak: [],
    goalStreak: [],
    journalTotal: [],
    goalTotal: [],
  });

  const [featuredBadge, setFeaturedBadge] = useState(null);

  // For unlock popup animation
  const [newlyUnlocked, setNewlyUnlocked] = useState([]);

  // ------------------------------------------------
  // Listen to journal entries for this user
  // ------------------------------------------------
  useEffect(() => {
    if (!user) return;

    setJournalLoading(true);
    const q = query(collection(db, "journal"), where("uid", "==", user.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => d.data());
        setJournalEntries(docs);
        setJournalLoading(false);
      },
      (err) => {
        console.error("Error loading journal entries for streaks:", err);
        setJournalLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ------------------------------------------------
  // Listen to goals for this user
  // ------------------------------------------------
  useEffect(() => {
    if (!user) return;

    setGoalLoading(true);
    const q = query(collection(db, "goals"), where("uid", "==", user.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGoalItems(docs);
        setGoalLoading(false);
      },
      (err) => {
        console.error("Error loading goals for streaks:", err);
        setGoalLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ------------------------------------------------
  // Listen to user profile to get premium + badges + featured badge
  // ------------------------------------------------
  useEffect(() => {
    if (!user) {
      setIsPremium(false);
      setProfileLoaded(true);
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        const data = snapshot.data() || {};
        setIsPremium(data.premium === true);
        setUserBadges({
          journalStreak: Array.isArray(data.badges?.journalStreak)
            ? data.badges.journalStreak
            : [],
          goalStreak: Array.isArray(data.badges?.goalStreak)
            ? data.badges.goalStreak
            : [],
          journalTotal: Array.isArray(data.badges?.journalTotal)
            ? data.badges.journalTotal
            : [],
          goalTotal: Array.isArray(data.badges?.goalTotal)
            ? data.badges.goalTotal
            : [],
        });
        setFeaturedBadge(data.featuredBadge || null);
        setProfileLoaded(true);
      },
      (err) => {
        console.error("Error loading user profile for streaks:", err);
        setIsPremium(false);
        setProfileLoaded(true);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ------------------------------------------------
  // Compute streaks and totals from loaded data
  // ------------------------------------------------
  useEffect(() => {
    const journalDates = journalEntries.map((e) =>
      e.createdAt?.toDate ? e.createdAt.toDate() : e.createdAt ? new Date(e.createdAt) : null
    );
    const { streak: jStreak, lastDate: jLast } = computeStreakFromDates(journalDates);
    setJournalStreak(jStreak);
    setLastJournalDate(jLast ? jLast.toDateString() : null);

    const goalDates = goalItems.map((g) =>
      g.createdAt?.toDate ? g.createdAt.toDate() : g.createdAt ? new Date(g.createdAt) : null
    );
    const { streak: gStreak, lastDate: gLast } = computeStreakFromDates(goalDates);
    setGoalStreak(gStreak);
    setLastGoalDate(gLast ? gLast.toDateString() : null);
  }, [journalEntries, goalItems]);

  const journalTotal = useMemo(() => journalEntries.length, [journalEntries]);
  const goalsCompletedTotal = useMemo(
    () => goalItems.filter((g) => g.done || g.completedAt).length,
    [goalItems]
  );

  // ------------------------------------------------
  // Award badges based on current stats and store in Firestore
  // Badges are permanent: we never remove them, only add new ones.
  // This runs for all users so if they regain premium, badges come back.
  // ------------------------------------------------
  useEffect(() => {
    if (!user || !profileLoaded) return;

    const ensureArray = (value) => (Array.isArray(value) ? value : []);

    const currentJournalStreakBadges = ensureArray(userBadges.journalStreak);
    const currentGoalStreakBadges = ensureArray(userBadges.goalStreak);
    const currentJournalTotalBadges = ensureArray(userBadges.journalTotal);
    const currentGoalTotalBadges = ensureArray(userBadges.goalTotal);

    const newJournalStreakBadges = JOURNAL_STREAK_BADGES.filter(
      (b) => journalStreak >= b.days && !currentJournalStreakBadges.includes(b.days)
    );
    const newGoalStreakBadges = GOAL_STREAK_BADGES.filter(
      (b) => goalStreak >= b.days && !currentGoalStreakBadges.includes(b.days)
    );
    const newJournalTotalBadges = JOURNAL_TOTAL_BADGES.filter(
      (b) => journalTotal >= b.total && !currentJournalTotalBadges.includes(b.total)
    );
    const newGoalTotalBadges = GOAL_TOTAL_BADGES.filter(
      (b) =>
        goalsCompletedTotal >= b.total && !currentGoalTotalBadges.includes(b.total)
    );

    const hasAnyNew =
      newJournalStreakBadges.length ||
      newGoalStreakBadges.length ||
      newJournalTotalBadges.length ||
      newGoalTotalBadges.length;

    if (!hasAnyNew) return;

    const userRef = doc(db, "users", user.uid);

    const updates = {};
    if (newJournalStreakBadges.length) {
      updates["badges.journalStreak"] = arrayUnion(
        ...newJournalStreakBadges.map((b) => b.days)
      );
    }
    if (newGoalStreakBadges.length) {
      updates["badges.goalStreak"] = arrayUnion(
        ...newGoalStreakBadges.map((b) => b.days)
      );
    }
    if (newJournalTotalBadges.length) {
      updates["badges.journalTotal"] = arrayUnion(
        ...newJournalTotalBadges.map((b) => b.total)
      );
    }
    if (newGoalTotalBadges.length) {
      updates["badges.goalTotal"] = arrayUnion(
        ...newGoalTotalBadges.map((b) => b.total)
      );
    }

    const unlockedNow = [
      ...newJournalStreakBadges.map((b) => ({
        ...b,
        category: "journalStreak",
        value: b.days,
      })),
      ...newGoalStreakBadges.map((b) => ({
        ...b,
        category: "goalStreak",
        value: b.days,
      })),
      ...newJournalTotalBadges.map((b) => ({
        ...b,
        category: "journalTotal",
        value: b.total,
      })),
      ...newGoalTotalBadges.map((b) => ({
        ...b,
        category: "goalTotal",
        value: b.total,
      })),
    ];

    (async () => {
      try {
        await updateDoc(userRef, updates);
        setNewlyUnlocked(unlockedNow);
      } catch (err) {
        console.error("Error updating badge progress:", err);
      }
    })();
  }, [
    user,
    profileLoaded,
    userBadges,
    journalStreak,
    goalStreak,
    journalTotal,
    goalsCompletedTotal,
  ]);

  // ------------------------------------------------
  // Compute which badges are currently unlocked based on stats
  // This uses actual activity so it always matches user progress.
  // ------------------------------------------------
  const unlockedJournalStreakBadges = useMemo(
    () => JOURNAL_STREAK_BADGES.filter((b) => journalStreak >= b.days),
    [journalStreak]
  );
  const unlockedGoalStreakBadges = useMemo(
    () => GOAL_STREAK_BADGES.filter((b) => goalStreak >= b.days),
    [goalStreak]
  );
  const unlockedJournalTotalBadges = useMemo(
    () => JOURNAL_TOTAL_BADGES.filter((b) => journalTotal >= b.total),
    [journalTotal]
  );
  const unlockedGoalTotalBadges = useMemo(
    () => GOAL_TOTAL_BADGES.filter((b) => goalsCompletedTotal >= b.total),
    [goalsCompletedTotal]
  );

  const loading = journalLoading || goalLoading || !profileLoaded;

  // ------------------------------------------------
  // Set featured badge in Firestore when user clicks one
  // Only allowed if user is premium and badge is unlocked
  // ------------------------------------------------
  const handleSetFeaturedBadge = async (badge, category) => {
    if (!user || !isPremium) return;

    const userRef = doc(db, "users", user.uid);
    const payload = {
      category,
      value: badge.days ?? badge.total,
      label: badge.label,
      emoji: badge.emoji,
    };

    try {
      await updateDoc(userRef, { featuredBadge: payload });
      setFeaturedBadge(payload);
    } catch (err) {
      console.error("Error setting featured badge:", err);
    }
  };

  // ------------------------------------------------
  // Render helpers
  // ------------------------------------------------
  const renderBadgeGrid = (allBadges, unlockedBadges, typeLabel, category) => {
    const unlockedSet = new Set(
      unlockedBadges.map((b) => (b.days ?? b.total))
    );

    return (
      <div
        style={{
          marginTop: "1.5rem",
          textAlign: "left",
        }}
      >
        <h3
          style={{
            color: "var(--primary)",
            marginBottom: "0.5rem",
            fontSize: "1.1rem",
          }}
        >
          {typeLabel}
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "12px",
          }}
        >
          {allBadges.map((badge) => {
            const keyValue = badge.days ?? badge.total;
            const unlocked = unlockedSet.has(keyValue);
            const isFeatured =
              featuredBadge &&
              featuredBadge.category === category &&
              featuredBadge.value === keyValue;

            const clickable = isPremium && unlocked;

            return (
              <button
                key={`${category}-${keyValue}`}
                type="button"
                onClick={() => clickable && handleSetFeaturedBadge(badge, category)}
                style={{
                  textAlign: "left",
                  cursor: clickable ? "pointer" : "default",
                  background: unlocked
                    ? "var(--card)"
                    : "rgba(255,255,255,0.03)",
                  borderRadius: "10px",
                  padding: "10px 12px",
                  border: isFeatured
                    ? "1px solid var(--primary)"
                    : unlocked
                    ? "1px solid rgba(255,255,255,0.18)"
                    : "1px solid rgba(255,255,255,0.06)",
                  boxShadow: isFeatured
                    ? "0 0 16px rgba(160,32,240,0.7)"
                    : unlocked
                    ? "0 0 10px rgba(0,0,0,0.4)"
                    : "0 0 6px rgba(0,0,0,0.25)",
                  opacity: unlocked ? 1 : 0.6,
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  transform: clickable ? "translateY(0)" : "none",
                }}
              >
                <div
                  style={{
                    fontSize: "1.4rem",
                    marginBottom: "2px",
                  }}
                >
                  {badge.emoji}
                </div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "var(--text)",
                  }}
                >
                  {badge.label}
                </div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "var(--muted)",
                  }}
                >
                  {badge.days != null
                    ? `${badge.days} day streak`
                    : `${badge.total} total`}
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    marginTop: "4px",
                    color: unlocked ? "var(--primary)" : "var(--muted)",
                  }}
                >
                  {unlocked
                    ? clickable
                      ? isFeatured
                        ? "Featured badge"
                        : "Tap to feature"
                      : "Unlocked"
                    : "Locked"}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ------------------------------------------------
  // If not signed in
  // ------------------------------------------------
  if (!user) {
    return (
      <>
        <Helmet>
          <title>Streaks – ReflectRise</title>
          <link rel="canonical" href="https://reflectrise.co.uk/streaks" />
        </Helmet>
        <div
          className="streaks-page"
          style={{
            background: "var(--bg)",
            color: "var(--text)",
            minHeight: "100vh",
            padding: "2rem 1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="streaks-card"
            style={{
              background: "var(--card)",
              padding: "2rem",
              borderRadius: "16px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
              maxWidth: "480px",
              width: "100%",
              textAlign: "center",
            }}
          >
            <h2 style={{ color: "var(--primary)" }}>Streaks</h2>
            <p style={{ color: "var(--muted)", marginTop: "0.5rem" }}>
              Please sign in to view your streaks and badges.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Streaks – ReflectRise</title>
        <meta
          name="description"
          content="Track your journaling and goal streaks with ReflectRise. Stay consistent, unlock badges, and keep your momentum going."
        />
        <link rel="canonical" href="https://reflectrise.co.uk/streaks" />
        {/* Simple keyframes for badge unlock popup */}
        <style>{`
          @keyframes badge-pop {
            0% { transform: scale(0.85); opacity: 0; }
            60% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </Helmet>

      <div
        className="streaks-page"
        style={{
          background: "var(--bg)",
          color: "var(--text)",
          minHeight: "100vh",
          padding: "2rem 1rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          className="streaks-card"
          style={{
            background: "var(--card)",
            padding: "1.8rem 1.6rem",
            borderRadius: "16px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
            textAlign: "center",
            maxWidth: "900px",
            width: "100%",
          }}
        >
          <h2 style={{ color: "var(--primary)", marginBottom: "0.5rem" }}>
            🔥 Streaks and Badges
          </h2>

          {loading ? (
            <p style={{ color: "var(--muted)" }}>Loading your streaks...</p>
          ) : (
            <>
              {/* Featured badge bar */}
              {isPremium && featuredBadge && (
                <div
                  style={{
                    marginTop: "0.75rem",
                    marginBottom: "0.75rem",
                    padding: "8px 12px",
                    borderRadius: "999px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background:
                      "linear-gradient(135deg, rgba(160,32,240,0.35), rgba(255,93,233,0.25))",
                    boxShadow: "0 0 14px rgba(0,0,0,0.5)",
                  }}
                >
                  <span style={{ fontSize: "1.4rem" }}>{featuredBadge.emoji}</span>
                  <span
                    style={{
                      fontSize: "0.9rem",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    Featured badge: {featuredBadge.label}
                  </span>
                </div>
              )}

              {/* Top summary row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "16px",
                  marginTop: "1rem",
                  marginBottom: "1.5rem",
                  textAlign: "left",
                }}
              >
                {/* Journal streak summary */}
                <div
                  style={{
                    background: "rgba(0,0,0,0.35)",
                    borderRadius: "12px",
                    padding: "12px 14px",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--muted)",
                      marginBottom: "4px",
                    }}
                  >
                    Journal streak
                  </div>
                  <div
                    style={{
                      fontSize: "2.2rem",
                      fontWeight: "bold",
                      color: "var(--primary)",
                      lineHeight: 1.1,
                    }}
                  >
                    {journalStreak}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "var(--muted)",
                      marginTop: "4px",
                    }}
                  >
                    {journalStreak > 0
                      ? `You have written for ${journalStreak} day${
                          journalStreak > 1 ? "s" : ""
                        } in a row`
                      : "No active journal streak yet"}
                  </div>
                  {lastJournalDate && (
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "var(--muted)",
                        marginTop: "4px",
                      }}
                    >
                      Last journal: <strong>{lastJournalDate}</strong>
                    </div>
                  )}
                </div>

                {/* Goal streak summary */}
                <div
                  style={{
                    background: "rgba(0,0,0,0.35)",
                    borderRadius: "12px",
                    padding: "12px 14px",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--muted)",
                      marginBottom: "4px",
                    }}
                  >
                    Goal streak
                  </div>
                  <div
                    style={{
                      fontSize: "2.2rem",
                      fontWeight: "bold",
                      color: "var(--primary)",
                      lineHeight: 1.1,
                    }}
                  >
                    {goalStreak}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "var(--muted)",
                      marginTop: "4px",
                    }}
                  >
                    {goalStreak > 0
                      ? `You have been active with goals for ${goalStreak} day${
                          goalStreak > 1 ? "s" : ""
                        } in a row`
                      : "No active goal streak yet"}
                  </div>
                  {lastGoalDate && (
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "var(--muted)",
                        marginTop: "4px",
                      }}
                    >
                      Last goal activity: <strong>{lastGoalDate}</strong>
                    </div>
                  )}
                </div>

                {/* Totals */}
                <div
                  style={{
                    background: "rgba(0,0,0,0.35)",
                    borderRadius: "12px",
                    padding: "12px 14px",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--muted)",
                      marginBottom: "4px",
                    }}
                  >
                    Totals
                  </div>
                  <div
                    style={{
                      fontSize: "0.95rem",
                      color: "var(--text)",
                    }}
                  >
                    ✏️ Journal entries: <strong>{journalTotal}</strong>
                  </div>
                  <div
                    style={{
                      fontSize: "0.95rem",
                      color: "var(--text)",
                      marginTop: "4px",
                    }}
                  >
                    🏁 Goals completed: <strong>{goalsCompletedTotal}</strong>
                  </div>
                </div>
              </div>

              {/* Premium gate for badges */}
              {!isPremium && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    borderRadius: "12px",
                    border: "1px dashed rgba(255,255,255,0.2)",
                    background:
                      "linear-gradient(135deg, rgba(160,32,240,0.12), rgba(255,93,233,0.08))",
                    textAlign: "center",
                  }}
                >
                  <h3
                    style={{
                      marginBottom: "0.5rem",
                      color: "var(--primary)",
                      fontSize: "1.05rem",
                    }}
                  >
                    Premium badges
                  </h3>
                  <p
                    style={{
                      color: "var(--muted)",
                      fontSize: "0.9rem",
                      marginBottom: "0.75rem",
                    }}
                  >
                    Badges for streaks and goals are a premium feature. Upgrade to unlock your full badge collection.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "8px",
                      marginBottom: "0.75rem",
                      fontSize: "1.4rem",
                      opacity: 0.85,
                    }}
                  >
                    <span>🌱</span>
                    <span>🔥</span>
                    <span>⭐</span>
                    <span>💎</span>
                    <span>👑</span>
                    <span>🚀</span>
                  </div>
                  <button
                    className="btn"
                    style={{
                      padding: "8px 16px",
                      borderRadius: "999px",
                    }}
                    onClick={() => {
                      window.location.href = "/premium";
                    }}
                  >
                    View Premium
                  </button>
                </div>
              )}

              {isPremium && (
                <>
                  {renderBadgeGrid(
                    JOURNAL_STREAK_BADGES,
                    unlockedJournalStreakBadges,
                    "Journal streak badges",
                    "journalStreak"
                  )}

                  {renderBadgeGrid(
                    GOAL_STREAK_BADGES,
                    unlockedGoalStreakBadges,
                    "Goal streak badges",
                    "goalStreak"
                  )}

                  {renderBadgeGrid(
                    JOURNAL_TOTAL_BADGES,
                    unlockedJournalTotalBadges,
                    "Journal entry badges",
                    "journalTotal"
                  )}

                  {renderBadgeGrid(
                    GOAL_TOTAL_BADGES,
                    unlockedGoalTotalBadges,
                    "Completed goal badges",
                    "goalTotal"
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Unlock popup animation */}
        {isPremium && newlyUnlocked.length > 0 && (
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: "20px",
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                pointerEvents: "auto",
                background: "var(--card)",
                borderRadius: "16px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
                border: "1px solid var(--primary)",
                animation: "badge-pop 0.4s ease-out",
                maxWidth: "90%",
              }}
            >
              <div style={{ fontSize: "1.8rem" }}>
                {newlyUnlocked[0].emoji}
              </div>
              <div style={{ textAlign: "left" }}>
                <div
                  style={{
                    fontSize: "0.9rem",
                    color: "var(--primary)",
                    fontWeight: 600,
                  }}
                >
                  New badge unlocked
                </div>
                <div
                  style={{
                    fontSize: "0.9rem",
                    color: "var(--text)",
                  }}
                >
                  {newlyUnlocked[0].label}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setNewlyUnlocked([])}
                style={{
                  marginLeft: "8px",
                  border: "none",
                  borderRadius: "999px",
                  padding: "4px 8px",
                  fontSize: "0.75rem",
                  background: "rgba(255,255,255,0.08)",
                  color: "var(--text)",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
